/**
 * Registration page for unclassified users.
 * 
 * REQ-AUTH-SIGNUP-EMAIL-ONLY-001: Signup flow accepts email only, no registration_kind selection.
 * REQ-AUTH-SIGNUP-OTP-ONLY-001: Signup uses email OTP verification.
 * REQ-AUTH-REGISTRATION-KIND-UNCLASSIFIED-001: New accounts created with registration_kind = unclassified.
 * REQ-AUTH-OTP-SUCCESS-HOME-REDIRECT-001: After OTP verification, user lands on home page.
 * 
 * Privacy posture: Anonymous/pseudonymous - no legal name collection.
 * Email is pseudonymous identifier.
 * Fingerprint captured for audit context, not identity.
 * 
 * SECURITY: Authenticated users are redirected to home.
 * The server session cookie is the authoritative source of truth.
 * Only show registration form when server has confirmed no valid session.
 */
import { useState, FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { registerWithOTP, verifyOTP, resendVerification, AuthError, isValidEmail, isSafeDisplayableError, FingerprintSignals } from '@/api/auth';
import { collectFingerprint } from '@/utils/fingerprint';
import { useAuthStore } from '@/store/authStore';

type FormState = 'idle' | 'submitting' | 'success' | 'error';
type ResendState = 'idle' | 'sending' | 'sent' | 'error';

export function RegisterPage() {
  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  // OTP verification state
  const [otpCode, setOTPCode] = useState('');
  const [showOTPInput, setShowOTPInput] = useState(false);
  
  // Resend verification state
  const [resendState, setResendState] = useState<ResendState>('idle');
  const [resendError, setResendError] = useState('');
  const [fallbackEmail, setFallbackEmail] = useState('');

  // Get auth status from store - only redirect when server has validated session
  const authStatus = useAuthStore((state) => state.authStatus);

  // SECURITY: Redirect authenticated users to home
  // authStatus 'authenticated' means server validated the session
  // authStatus 'loading' means bootstrap in progress - don't redirect yet
  if (authStatus === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (!isValidEmail(email)) {
      setFormState('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setFormState('submitting');
    setErrorMessage('');

    // Collect fingerprint for fraud-resistant audit context (fail-open)
    let fingerprint: FingerprintSignals | undefined;
    try {
      fingerprint = await collectFingerprint();
    } catch {
      // Fingerprint collection failed - proceed without fingerprint
    }

    try {
      // REQ-AUTH-SIGNUP-OTP-ONLY-001: Use OTP-based registration
      await registerWithOTP(email, fingerprint);
      setFormState('success');
      setShowOTPInput(true);
    } catch (err) {
      setFormState('error');
      if (err instanceof AuthError) {
        const statusMsg = err.status ? ` (HTTP ${err.status})` : '';
        if (isSafeDisplayableError(err.message)) {
          setErrorMessage(err.message);
        } else {
          setErrorMessage('Registration failed' + statusMsg + '. Please try again later.');
        }
      } else if (err instanceof Error) {
        setErrorMessage('Registration failed. Please try again later.');
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    }
  };

  const handleVerifyOTP = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!otpCode.trim()) {
      setErrorMessage('Please enter the verification code');
      return;
    }

    setFormState('submitting');
    setErrorMessage('');

    // Collect fingerprint for audit context
    let fingerprint: FingerprintSignals | undefined;
    try {
      fingerprint = await collectFingerprint();
    } catch {
      // Fingerprint collection failed - proceed without fingerprint
    }

    try {
      // REQ-AUTH-OTP-SUCCESS-HOME-REDIRECT-001: Verify OTP and redirect to home
      await verifyOTP(email, otpCode, fingerprint);
      // Success - redirect to home page
      window.location.href = '/';
    } catch (err) {
      setFormState('error');
      if (err instanceof AuthError) {
        const statusMsg = err.status ? ` (HTTP ${err.status})` : '';
        if (isSafeDisplayableError(err.message)) {
          setErrorMessage(err.message);
        } else {
          setErrorMessage('Verification failed' + statusMsg + '. Please try again.');
        }
      } else if (err instanceof Error) {
        setErrorMessage('Verification failed. Please try again later.');
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    }
  };

  const handleResend = async () => {
    const emailToUse = email || fallbackEmail;
    
    if (!isValidEmail(emailToUse)) {
      setResendError('Enter a valid email address.');
      return;
    }

    setResendState('sending');
    setResendError('');

    let fingerprint: FingerprintSignals | undefined;
    try {
      fingerprint = await collectFingerprint();
    } catch {
      // Fingerprint collection failed - proceed without fingerprint
    }

    try {
      await resendVerification(emailToUse, fingerprint);
      setResendState('sent');
    } catch {
      setResendState('error');
      setResendError("We couldn't process the request right now. Please try again later.");
    }
  };

  const handleReset = () => {
    setEmail('');
    setOTPCode('');
    setShowOTPInput(false);
    setFormState('idle');
    setErrorMessage('');
    setResendState('idle');
    setResendError('');
    setFallbackEmail('');
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Create Account</h1>
        <p className="page-description">
          Join InDeep. Your email is your only identifier.
        </p>
      </div>

      {/* OTP verification step */}
      {formState === 'success' && showOTPInput ? (
        <div className="form-section">
          <div className="success-box">
            <p className="success-title">Enter verification code</p>
            <p className="success-text">
              We've sent a verification code to {email}. Enter it below to activate your account.
            </p>
          </div>
          
          <form onSubmit={handleVerifyOTP} className="otp-form">
            <div className="form-group">
              <label htmlFor="otp-code" className="form-label">Verification Code</label>
              <input
                id="otp-code"
                type="text"
                name="code"
                value={otpCode}
                onChange={(e) => setOTPCode(e.target.value)}
                placeholder="Enter 4-digit code"
                className="form-input otp-input"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                disabled={formState === 'submitting'}
                required
              />
            </div>

            {formState === 'error' && errorMessage && (
              <div className="error-box" role="alert">
                <p>{errorMessage}</p>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={formState === 'submitting' || !otpCode.trim()}
            >
              {formState === 'submitting' ? 'Verifying...' : 'Verify'}
            </button>
          </form>
          
          {/* Resend section */}
          {resendState === 'idle' && (
            <div className="resend-section">
              <p className="resend-label">Didn't receive the code?</p>
              <button
                type="button"
                className="btn btn-link"
                onClick={handleResend}
              >
                Resend verification code
              </button>
              {resendError && (
                <p className="resend-error">{resendError}</p>
              )}
            </div>
          )}
          
          {resendState === 'sending' && (
            <div className="resend-section">
              <p className="resend-status">Sending…</p>
            </div>
          )}
          
          {resendState === 'sent' && (
            <div className="resend-section">
              <p className="resend-success">
                If this email is eligible, we'll send a new verification code.
              </p>
            </div>
          )}
          
          {resendState === 'error' && (
            <div className="resend-section">
              <p className="resend-error">{resendError}</p>
              <button
                type="button"
                className="btn btn-link"
                onClick={() => {
                  setResendState('idle');
                  setResendError('');
                }}
              >
                Try again
              </button>
            </div>
          )}
          
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={handleReset}
          >
            Use different email
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="form-section">
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="form-input"
              autoComplete="email"
              disabled={formState === 'submitting'}
              required
            />
          </div>

          {formState === 'error' && errorMessage && (
            <div className="error-box" role="alert">
              <p>{errorMessage}</p>
            </div>
          )}

          <div className="privacy-note">
            <p>
              <strong>Privacy note:</strong> We don't collect your name, phone,
              or address. Your email serves as a pseudonymous identifier for
              access only.
            </p>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={formState === 'submitting' || !email.trim()}
          >
            {formState === 'submitting' ? 'Sending...' : 'Create Account'}
          </button>

          <p className="form-footer">
            Already have an account?{' '}
            <a href="/login">Sign in</a>
          </p>
        </form>
      )}

      <style>{`
        .form-section {
          margin-top: var(--spacing);
        }
        
        .form-group {
          margin-bottom: var(--spacing);
        }
        
        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
        }
        
        .form-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background-color: var(--color-surface);
          color: var(--color-text);
          font-size: 16px;
        }
        
        .form-input:focus {
          outline: none;
          border-color: var(--color-accent);
        }
        
        .form-input::placeholder {
          color: var(--color-text-muted);
        }
        
        .form-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .otp-input {
          letter-spacing: 0.2em;
          font-size: 1.5rem;
          text-align: center;
        }
        
        .btn {
          width: 100%;
          padding: 14px 24px;
          border: none;
          border-radius: var(--radius);
          font-size: 16px;
          font-weight: 600;
          transition: opacity 0.2s ease;
        }
        
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .btn-primary {
          background-color: var(--color-accent);
          color: white;
          margin-bottom: var(--spacing);
        }
        
        .btn-primary:hover:not(:disabled) {
          opacity: 0.9;
        }
        
        .btn-secondary {
          background-color: transparent;
          color: var(--color-accent);
          border: 1px solid var(--color-accent);
        }
        
        .btn-secondary:hover:not(:disabled) {
          background-color: var(--color-accent);
          color: white;
        }
        
        .error-box {
          padding: 12px 16px;
          background-color: rgba(233, 69, 96, 0.1);
          border: 1px solid var(--color-accent);
          border-radius: var(--radius);
          margin-bottom: var(--spacing);
          color: var(--color-accent);
        }
        
        .success-box {
          padding: 20px;
          background-color: rgba(46, 204, 113, 0.1);
          border: 1px solid #2ecc71;
          border-radius: var(--radius);
          margin-bottom: var(--spacing);
          text-align: center;
        }
        
        .success-title {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 8px;
          color: #2ecc71;
        }
        
        .success-text {
          color: var(--color-text-muted);
        }
        
        .privacy-note {
          font-size: 0.875rem;
          color: var(--color-text-muted);
          margin-bottom: var(--spacing);
          line-height: 1.5;
        }
        
        .privacy-note strong {
          color: var(--color-text);
        }
        
        .form-footer {
          text-align: center;
          color: var(--color-text-muted);
        }
        
        .form-footer a {
          color: var(--color-accent);
          font-weight: 500;
        }
        
        .otp-form {
          margin-bottom: var(--spacing);
        }
        
        /* Resend verification section */
        .resend-section {
          text-align: center;
          margin-bottom: var(--spacing);
          padding: 16px;
          background-color: rgba(233, 69, 96, 0.05);
          border-radius: var(--radius);
        }
        
        .resend-label {
          color: var(--color-text-muted);
          margin-bottom: 12px;
          font-size: 0.875rem;
        }
        
        .btn-link {
          width: auto;
          padding: 8px 16px;
          background: none;
          border: none;
          color: var(--color-accent);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          text-decoration: underline;
        }
        
        .btn-link:hover:not(:disabled) {
          opacity: 0.8;
        }
        
        .btn-link:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .resend-status {
          color: var(--color-text-muted);
          font-size: 0.875rem;
        }
        
        .resend-success {
          color: #2ecc71;
          font-size: 0.875rem;
          font-weight: 500;
        }
        
        .resend-error {
          color: var(--color-accent);
          font-size: 0.875rem;
          margin-top: 8px;
        }
      `}</style>
    </div>
  );
}
